import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Paper,
  Grid,
  Button,
  Card,
  CardContent,
  Divider,
  Chip
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Cancel as WrongIcon,
  ArrowBack as BackIcon,
  Psychology as AIIcon
} from '@mui/icons-material';
import leaksService from '../services/leaksService';

const LeaksResults = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [results, setResults] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingFeedback, setLoadingFeedback] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadResults();
  }, [sessionId]);

  const loadResults = async () => {
    try {
      setLoading(true);
      const response = await leaksService.getResults(sessionId);
      setResults(response.data);
      setFeedback(response.data.feedback);
      setError(null);
    } catch (err) {
      console.error('Error loading results:', err);
      setError('Failed to load results. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateFeedback = async () => {
    try {
      setLoadingFeedback(true);
      const response = await leaksService.generateFeedback(sessionId);
      setFeedback(response.data);
    } catch (err) {
      console.error('Error generating feedback:', err);
      alert('Failed to generate feedback. Please try again.');
    } finally {
      setLoadingFeedback(false);
    }
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error || !results) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error">{error || 'Results not found'}</Alert>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate('/leaks')}
          sx={{ mt: 2 }}
        >
          Back to Past Papers
        </Button>
      </Container>
    );
  }

  const { test, results: scores, questions } = results;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Button
        startIcon={<BackIcon />}
        onClick={() => navigate('/leaks')}
        sx={{ mb: 3 }}
      >
        Back to Leaks
      </Button>

      <Typography variant="h4" gutterBottom fontWeight="bold">
        Test Results: {test.name}
      </Typography>

      {/* Score Summary */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Paper elevation={2} sx={{ p: 3, textAlign: 'center', bgcolor: '#e3f2fd' }}>
            <Typography variant="h6" gutterBottom>Reading & Writing</Typography>
            <Typography variant="h4" fontWeight="bold" color="#1976d2">
              {scores.readingWriting.percentage?.toFixed(1)}%
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {scores.readingWriting.correct}/{scores.readingWriting.total} correct
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper elevation={2} sx={{ p: 3, textAlign: 'center', bgcolor: '#e8f5e9' }}>
            <Typography variant="h6" gutterBottom>Math</Typography>
            <Typography variant="h4" fontWeight="bold" color="#2e7d32">
              {scores.math.percentage?.toFixed(1)}%
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {scores.math.correct}/{scores.math.total} correct
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper elevation={2} sx={{ p: 3, textAlign: 'center', bgcolor: '#fff3e0' }}>
            <Typography variant="h6" gutterBottom>Overall</Typography>
            <Typography variant="h4" fontWeight="bold" color="#f57c00">
              {scores.overall.percentage?.toFixed(1)}%
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {scores.overall.correct}/{scores.overall.total} correct
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* AI Feedback */}
      {!feedback && (
        <Box sx={{ mb: 4 }}>
          <Button
            variant="contained"
            startIcon={<AIIcon />}
            onClick={handleGenerateFeedback}
            disabled={loadingFeedback}
            sx={{ bgcolor: '#9c27b0', '&:hover': { bgcolor: '#7b1fa2' } }}
          >
            {loadingFeedback ? 'Generating...' : 'Generate AI Feedback'}
          </Button>
        </Box>
      )}

      {feedback && (
        <Paper elevation={2} sx={{ p: 3, mb: 4, bgcolor: '#f3e5f5' }}>
          <Typography variant="h6" gutterBottom fontWeight="bold" color="#9c27b0">
            <AIIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
            AI Feedback
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
            {feedback.feedbackText}
          </Typography>
        </Paper>
      )}

      {/* Questions Review */}
      <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ mb: 3 }}>
        Questions Review
      </Typography>

      {questions && questions.map((q, index) => (
        <Card key={index} sx={{ mb: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Typography variant="h6">
                Question {q.questionNumber} ({q.module.type} Module {q.module.number})
              </Typography>
              <Chip
                icon={q.isCorrect ? <CheckIcon /> : <WrongIcon />}
                label={q.isCorrect ? 'Correct' : 'Incorrect'}
                color={q.isCorrect ? 'success' : 'error'}
              />
            </Box>

            {q.article && (
              <Box sx={{ mb: 2 }}>
                <div dangerouslySetInnerHTML={{ __html: q.article }} />
              </Box>
            )}

            {q.question && (
              <Typography variant="body1" sx={{ mb: 2 }}>
                {q.question}
              </Typography>
            )}

            {q.type === 'choice' && (
              <Box sx={{ mb: 2 }}>
                {['A', 'B', 'C', 'D'].map((opt) => {
                  const optionText = q[`option${opt}`];
                  if (!optionText) return null;
                  const isStudentAnswer = q.studentAnswer === optionText;
                  const isCorrectAnswer = q.correctAnswer === optionText;
                  
                  return (
                    <Box
                      key={opt}
                      sx={{
                        p: 1,
                        mb: 1,
                        borderRadius: 1,
                        bgcolor: isCorrectAnswer
                          ? '#e8f5e9'
                          : isStudentAnswer
                          ? '#ffebee'
                          : '#f5f5f5',
                        border: isCorrectAnswer ? '2px solid #4caf50' : '1px solid #ddd'
                      }}
                    >
                      <div dangerouslySetInnerHTML={{ __html: `<strong>${opt}:</strong> ${optionText}` }} />
                    </Box>
                  );
                })}
              </Box>
            )}

            {q.type === 'write' && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Your answer: <strong>{q.studentAnswer || 'No answer'}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Correct answer: <strong>{q.correctAnswer}</strong>
                </Typography>
              </Box>
            )}

            {q.solution && (
              <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                  Explanation:
                </Typography>
                <div dangerouslySetInnerHTML={{ __html: q.solution }} />
              </Box>
            )}
          </CardContent>
        </Card>
      ))}
    </Container>
  );
};

export default LeaksResults;
